/**
 * Created by Andrey Popov on 10/2/25.
 */

const PathIndicator = function (map) {
    this.map = map;

    this.pointsView = undefined;
    this.targetView = undefined;
    this.bubbleView = undefined;
    this.pathInfoViews = undefined;

    this.currentPath = undefined;

    this.pathfinder = PathIndicator.createPathfinder(map);

    this.map.on("onPositionChanged", cleverapps.throttle(50, () => this.updateBubbleVisibility()), this);

    this.map.on("onUnitAvailable", (unit) => this.onUnitAvailable(unit), this);
    this.map.on("onUnitRemoved", (removedUnit) => this.onUnitRemoved(removedUnit), this);
    cleverapps.eventBus.on("mapEvent", (type, options) => this.onMapEvent(type, options), this);
    if (cleverapps.lives) {
        cleverapps.lives.on("changeAmount", () => {
            if (this.pathInfoViews) {
                this.pathInfoViews.onLivesAmountChanged();
            }
        }, this);
    }
};

PathIndicator.createPathfinder = function (map) {
    return new Pathfinder({
        cost: (x, y, prevX, prevY) => {
            const fogTile = map.getFog(x, y);
            if (fogTile && fogTile.fogBlock && fogTile.fogBlock.isMoney()) {
                return Pathfinder.IMPASSABLE;
            }

            const prevFog = map.getFog(prevX, prevY);
            const prevFogBlock = prevFog && prevFog.fogBlock && prevFog.fogBlock.id !== FogBlockedGround.ID
                ? prevFog.fogBlock : map.fogs.findAvailableFogBlock();
            if (fogTile && fogTile.fogBlock && fogTile.fogBlock.id !== FogBlockedGround.ID && prevFogBlock) {
                const fogId = fogTile.fogBlock.id.replace("fog", "");
                const prevFogId = prevFogBlock.id.replace("fog", "");
                if (cleverapps.isNumber(fogId) && cleverapps.isNumber(prevFogId) && Math.abs(parseInt(fogId) - parseInt(prevFogId)) > 1) {
                    return Pathfinder.IMPASSABLE;
                }
            }

            const mapUnit = map.isOccupiedByFog(x, y) ? map.fogs.getFakeUnit(x, y) : map.getUnit(x, y);
            const head = mapUnit && (mapUnit.head || mapUnit);
            if (mapUnit) {
                let passable = false;
                if (head.findComponent) {
                    const multicell = head.findComponent(MultiCell);
                    passable = multicell && multicell.canMoveThrough(x, y);
                } else {
                    const shape = Unit.GetShape(head);
                    if (shape && shape.some((part) => part.passable)) {
                        passable = shape.some((part) => part.x === x - head.x && part.y === y - head.y && part.passable);
                    }
                }
                if (passable) {
                    return PathIndicator.EMPTY_CELL_COST;
                }
            }

            if (fogTile) {
                if (!map.isGround(x, y) || map.isImpassableGround(x, y)) {
                    return Pathfinder.IMPASSABLE;
                }
                let fogBlockCoef = 1;
                if (fogTile.fogBlock && fogTile.fogBlock.id !== FogBlockedGround.ID) {
                    fogBlockCoef = PathIndicator.FOG_BLOCK_COEF;
                }
                return fogBlockCoef * (map.fogs.getFakeUnit(x, y) ? PathIndicator.FOG_UNIT_COST : PathIndicator.EMPTY_CELL_COST);
            }

            if (!map.isGround(x, y) && !map.isWaterUnit(x, y)) {
                return Pathfinder.IMPASSABLE;
            }

            if (!head) {
                return PathIndicator.EMPTY_CELL_COST;
            }

            if (head.isLockedUnit && head.isLockedUnit()) {
                return PathIndicator.LOCKED_UNIT_COST;
            }
            
            return PathIndicator.UNIT_COST;
        }
    });
};

PathIndicator.prototype.prePositionWorker = function () {
    const unit = this.getTargetUnit();
    const worker = this.map.workers.findFree();
    if (!unit || !worker || !worker.hasPosition()) {
        return;
    }

    const result = this.computePath(worker, unit);
    if (!result) {
        return;
    }

    const cell = worker.findNearestAvailableCell(result.path[0]);
    if (cell) {
        worker.position = cc.p(cell.x, cell.y);
        worker.onUpdateState();
    }
};

PathIndicator.prototype.showTargetCell = function (silent) {
    const unit = this.getTargetUnit();
    if (unit) {
        this.indicate(unit, { fromStored: true, silent });
    }
};

PathIndicator.prototype.getTargetCell = function () {
    return this.map.slot.load("local_pathindicator_cell");
};

PathIndicator.prototype.getTargetUnit = function () {
    const stored = this.getTargetCell();
    if (!stored || stored.x === undefined || stored.y === undefined) {
        return undefined;
    }

    const unit = this.map.getUnitWithHead(stored.x, stored.y);
    if (!unit && this.map.isOccupiedByFog(stored.x, stored.y)) {
        const fakeUnit = this.map.fogs.getFakeUnit(stored.x, stored.y);
        return fakeUnit && (fakeUnit.head || fakeUnit);
    }

    return unit;
};

PathIndicator.prototype.saveTargetCell = function (targetUnit) {
    this.map.slot.save("local_pathindicator_cell", { x: targetUnit.x, y: targetUnit.y });
};

PathIndicator.prototype.indicate = function (unit, options) {
    options = options || {};
    const worker = this.map.workers.findFree();
    if (!unit || !worker || !worker.hasPosition()) {
        return false;
    }

    let result = this.computePath(worker, unit);

    if (!result && options.alwaysShowIndicator) {
        result = { path: [{ x: unit.x, y: unit.y }], visualPath: [{ x: unit.x, y: unit.y }] };
    }

    if (!result) {
        return false;
    }

    this.clearIndicators(options.keepTarget, true);
    this.currentPath = result.path;
    this.currentVisualPath = result.visualPath;

    if (!options.fromStored) {
        this.saveTargetCell(unit);
    }

    if (options.silent) {
        this.createIndicators(unit, true);
    } else if (options.keepTarget) {
        this.createIndicators(unit);
    } else {
        this.map.focusOnUnit(unit, {
            skipFocusReport: true,
            allowScrollWithFocus: true,
            callback: () => {
                if (!this.destroyed && this.currentPath === result.path) {
                    this.createIndicators(unit, false, options.alwaysShowIndicator);
                }
            }
        });
    }

    return true;
};

PathIndicator.prototype.createIndicators = function (unit, silent, alwaysShowIndicator) {
    const path = this.currentPath;

    if (this.targetView) {
        this.showInfoViews(true);
    } else if (path.length > 1 || alwaysShowIndicator) {
        this.targetView = new PathTargetView(unit, {
            onShown: () => {
                this.showInfoViews(silent);
                this.updateBubbleVisibility();
            },
            onClick: () => {
                if (this.pointsView) {
                    this.pointsView.hide();
                    this.pointsView = undefined;
                } else {
                    this.indicate(unit, { keepTarget: true });
                }
            }
        });
    } else {
        FingerView.hintTap({ x: unit.x, y: unit.y }, { runOnce: true });
    }

    this.bubbleView = new PathBubbleView(unit, {
        onClick: () => {
            this.indicate(unit);
        }
    });

    const pointLimit = 1000;
    if (path.length > 5 && path.length < pointLimit && !silent) {
        this.pointsView = new PathPointsView(this.currentVisualPath.slice(PathIndicator.POINTS_START_OFFSET));
    }
};

PathIndicator.prototype.onMapEvent = function (type, options) {
    switch (type) {
        case Map2d.START_MINING:
        case Map2d.MINE:
            if (this.pointsView) {
                this.pointsView.hide();
                this.pointsView = undefined;
            }
            if (this.pathInfoViews && options.unit) {
                this.pathInfoViews.removeInfoViewForUnit(options.unit);
            }
            break;
        case Map2d.HARVEST:
            this.showInfoViews(true);
            break;
        case Map2d.EXCHANGE:
        case Map2d.FINISH_QUEST:
            if (this.targetView) {
                this.targetView.hide();
                this.targetView = undefined;
            }

            if (this.bubbleView) {
                this.bubbleView.hide();
                this.bubbleView = undefined;
            }
            break;
    }
};

PathIndicator.prototype.onUnitRemoved = function (removedUnit) {
    if (!this.currentPath) {
        return;
    }

    const isBody = removedUnit.isMultiCellBody();
    if (isBody) {
        removedUnit = removedUnit.head;
    }

    const removedUnitIndex = this.currentPath.findIndex((p) => p.x === removedUnit.x && p.y === removedUnit.y);
    if (removedUnitIndex === -1) {
        if (isBody) {
            return;
        }
        const worker = this.map.workers.findFree();
        if (worker && worker.hasPosition() && this.currentPath.length > 0) {
            const recomputeResult = this.computePath(worker, this.currentPath[this.currentPath.length - 1]);
            this.currentPath = recomputeResult ? recomputeResult.path : undefined;
            this.showInfoViews(true);
        }
    } else if (removedUnitIndex === this.currentPath.length - 1) {
        this.clearIndicators();
    } else {
        this.currentPath = this.sliceFromFirstMineable(this.currentPath.slice(removedUnitIndex + 1));
        this.showInfoViews(true);
        this.moveWorkerToNextTarget();
    }
};

PathIndicator.prototype.moveWorkerToNextTarget = function () {
    if (!cleverapps.config.debugMode) {
        return;
    }

    if (!this.currentPath || !this.map.workers.canWalk()) {
        return;
    }

    const unit = this.getMineable(this.currentPath[0]);
    if (!(unit instanceof Unit)) {
        return;
    }

    const worker = this.map.workers.findFree();
    if (!worker || !worker.hasPosition() || !worker.move) {
        return;
    }

    worker.move(cc.p(unit.x, unit.y));
};

PathIndicator.prototype.onUnitAvailable = function (unit) {
    const targetCell = this.getTargetCell();
    if (targetCell && targetCell.x === unit.x && targetCell.y === unit.y) {
        this.saveTargetCell({});
    }

    if (!this.currentPath || !this.currentPath.find((p) => p.x === unit.x && p.y === unit.y)) {
        return;
    }

    this.currentPath = this.sliceFromFirstMineable(this.currentPath);
    this.showInfoViews(true);
    this.moveWorkerToNextTarget();
};

PathIndicator.prototype.computePath = function (worker, unit) {
    const rawPath = this.pathfinder.findPath(worker.position, unit);

    const mappedPath = rawPath.map((p) => {
        const u = this.map.getUnitWithHead(p.x, p.y);
        if (u && u.isMultiCellBody()) {
            const multicell = u.head.findComponent && u.head.findComponent(MultiCell);
            if (multicell && multicell.canMoveThrough(p.x, p.y)) {
                return p;
            }
            return { x: u.head.x, y: u.head.y };
        }

        const fakeUnit = this.map.isOccupiedByFog(p.x, p.y) && this.map.fogs.getFakeUnit(p.x, p.y);
        if (fakeUnit && fakeUnit.head) {
            return { x: fakeUnit.head.x, y: fakeUnit.head.y };
        }

        return p;
    });

    const firstMineableIndex = mappedPath.findIndex((p) => this.getMineable(p) !== undefined);
    if (firstMineableIndex === -1) {
        this.clearIndicators();
        return undefined;
    }

    const firstFogIndex = mappedPath.findIndex((p) => this.map.isOccupiedByFog(p.x, p.y));
    let startIndex = firstMineableIndex;
    if (firstFogIndex > 0) {
        for (let i = firstFogIndex - 1; i >= 0; i--) {
            if (this.getMineable(mappedPath[i]) !== undefined) {
                startIndex = i;
                break;
            }
        }
    }

    return {
        path: mappedPath.slice(startIndex),
        visualPath: rawPath.slice(startIndex)
    };
};

PathIndicator.prototype.sliceFromFirstMineable = function (path) {
    const firstMineableIndex = path.findIndex((p) => this.getMineable(p) !== undefined);
    if (firstMineableIndex === -1) {
        this.clearIndicators();
        return undefined;
    }

    return path.slice(firstMineableIndex);
};

PathIndicator.prototype.getMineable = function (p) {
    let unit = this.map.getUnit(p.x, p.y);
    if (unit && unit.isMultiCellBody()) {
        const multicell = unit.head.findComponent && unit.head.findComponent(MultiCell);
        if (multicell && multicell.canMoveThrough(p.x, p.y)) {
            return undefined;
        }
        unit = unit.head;
    }
    if (unit && unit.findComponent(Mineable)) {
        return unit;
    }

    let fakeUnit = this.map.isOccupiedByFog(p.x, p.y) && this.map.fogs.getFakeUnit(p.x, p.y);
    if (fakeUnit && fakeUnit.head) {
        fakeUnit = fakeUnit.head;
    }
    if (fakeUnit && Families[fakeUnit.code].units[fakeUnit.stage].mineable !== undefined) {
        return fakeUnit;
    }
};

PathIndicator.prototype.showInfoViews = function (silent) {
    if (!this.currentPath) {
        return;
    }

    if (!this.pathInfoViews) {
        InfoView.CloseInfo();
        this.pathInfoViews = new PathInfoViews();
    }

    const pathUnits = [];
    let activeUnit = undefined;

    for (let i = 0; i < this.currentPath.length; i++) {
        const pathUnit = this.getMineable(this.currentPath[i]);
        if (pathUnit) {
            if (!activeUnit && pathUnit instanceof Unit) {
                activeUnit = pathUnit;
            }

            const firstUnit = activeUnit || this.currentPath[0];
            if (cc.pDistance(firstUnit, pathUnit) <= 4) {
                pathUnits.push(pathUnit);
            }

            if (pathUnit instanceof Unit && pathUnit.findComponent(Multiple)) {
                pathUnit.findComponent(Multiple).getInfoViews().hide();
            }
        }
    }

    const currentUnit = this.currentPath[0];

    if (silent) {
        if (Map2d.currentMap.isOccupiedByFog(currentUnit.x, currentUnit.y)) {
            return;
        }
        this.pathInfoViews.updateInfoViews(activeUnit, pathUnits);
    } else {
        const scrollTarget = activeUnit ? this.getOffsetScrollTarget(activeUnit) : currentUnit;
        this.map.focusOnUnit(scrollTarget, {
            skipFocusReport: true,
            allowScrollWithFocus: true,
            callback: () => {
                if (!this.destroyed && this.pathInfoViews) {
                    const fogTile = Map2d.currentMap.fogs.findTile(currentUnit.x, currentUnit.y);
                    if (fogTile && fogTile.fogBlock && !fogTile.fogBlock.isReady()) {
                        fogTile.fogBlock.hintTapBlocker();
                    } else {
                        if (activeUnit) {
                            this.pathInfoViews.lastScrolledUnit = activeUnit;
                        }
                        this.pathInfoViews.updateInfoViews(activeUnit, pathUnits);
                    }
                }
            }
        });
    }
};

PathIndicator.prototype.getOffsetScrollTarget = function (target) {
    const worker = this.map.workers.regular.find((w) => w.hasPosition());
    if (!worker) {
        return target;
    }
    const dx = worker.position.x - target.x;
    const dy = worker.position.y - target.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) {
        return target;
    }
    const offset = Math.min(1, dist);
    return { x: target.x + dx / dist * offset, y: target.y + dy / dist * offset };
};

PathIndicator.prototype.isInfoViewShown = function (unit) {
    return this.pathInfoViews && this.pathInfoViews.getUnitInfoView(unit) !== undefined;
};

PathIndicator.prototype.updateBubbleVisibility = function () {
    if (this.bubbleView) {
        this.bubbleView.checkTargetUnitVisibility();
    }
};

PathIndicator.prototype.showCustomerBubble = function (unit, checkmark) {
    if (this.bubbleView && this.customerBubbleUnit === unit && this.customerBubbleCheckmark === checkmark) {
        return;
    }
    if (this.bubbleView) {
        this.bubbleView.hide(true);
    }
    this.customerBubbleUnit = unit;
    this.customerBubbleCheckmark = checkmark;
    this.bubbleView = new PathBubbleView(unit, {
        checkmark,
        onClick: () => {
            this.map.focusOnUnit(unit, { skipFocusReport: true, allowScrollWithFocus: true });
        }
    });
};

PathIndicator.prototype.clearIndicators = function (keepTarget, skipCustomerBubble) {
    if (this.pointsView) {
        this.pointsView.hide(true);
        this.pointsView = undefined;
    }

    if (this.targetView && !keepTarget) {
        this.targetView.hide(true);
        this.targetView = undefined;
    }

    if (this.bubbleView) {
        this.bubbleView.hide(true);
        this.bubbleView = undefined;
    }

    if (this.pathInfoViews) {
        this.pathInfoViews.hide(true);
        this.pathInfoViews = undefined;
    }

    this.currentPath = undefined;
    this.currentVisualPath = undefined;
    this.customerBubbleUnit = undefined;
    this.customerBubbleCheckmark = undefined;

    if (!skipCustomerBubble && Map2d.currentMap && Map2d.currentMap.customers) {
        Map2d.currentMap.customers.updateCustomerBubble();
    }
};

PathIndicator.prototype.destructor = function () {
    this.destroyed = true;
    this.clearIndicators(undefined, true);
    runCleaners(this);
};

PathIndicator.POINTS_START_OFFSET = 2;

PathIndicator.EMPTY_CELL_COST = 1;
PathIndicator.UNIT_COST = 10;
PathIndicator.LOCKED_UNIT_COST = 15;
PathIndicator.FOG_UNIT_COST = 12;
PathIndicator.FOG_BLOCK_COEF = 4;